<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

import { reportMethods, reportComputed } from "@/state/helpers";

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}
function monthStartIso() {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default {
    page: {
        title: "Sales Report",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: { Layout, PageHeader, DatePicker },
    data() {
        return {
            title: "Sales Report",
            items: [{ text: "Reports" }, { text: "Sales", active: true }],
            range: [monthStartIso(), todayIso()],
            bucket: "day",
            limit: 20,
            topProductFields: [
                { key: "sku", label: "SKU" },
                { key: "name", label: "Product" },
                { key: "qty_sold", label: "Qty", class: "text-right" },
                { key: "revenue_approx", label: "Revenue", formatter: this.fmtMoney, class: "text-right" },
            ],
            topGroupFields: [
                { key: "name", label: "Group" },
                { key: "qty_sold", label: "Qty", class: "text-right" },
                { key: "revenue_approx", label: "Revenue", formatter: this.fmtMoney, class: "text-right" },
            ],
            exporting: false,
        };
    },
    computed: {
        ...reportComputed,
        fromIso() {
            const v = this.range && this.range[0];
            return v instanceof Date ? v.toISOString().slice(0, 10) : v;
        },
        toIso() {
            const v = this.range && this.range[1];
            return v instanceof Date ? v.toISOString().slice(0, 10) : v;
        },
        params() {
            return { from: this.fromIso, to: this.toIso, bucket: this.bucket };
        },
        kpis() {
            return (this.sales_summary && this.sales_summary.kpis) || { total_revenue: 0, order_count: 0, aov: 0, units_sold: 0 };
        },
        series() {
            return (this.sales_summary && this.sales_summary.series) || [];
        },
        chartSeries() {
            return [
                { name: "Revenue", type: "column", data: this.series.map((b) => [b.bucket, b.revenue]) },
                { name: "Orders", type: "line", data: this.series.map((b) => [b.bucket, b.order_count]) },
            ];
        },
        chartOptions() {
            return {
                chart: { type: "line", toolbar: { show: false } },
                stroke: { width: [0, 3] },
                xaxis: { type: "category", categories: this.series.map((b) => b.bucket) },
                yaxis: [
                    { title: { text: "Revenue" }, labels: { formatter: (v) => this.fmtMoney(v) } },
                    { opposite: true, title: { text: "Orders" } },
                ],
                tooltip: { shared: true, intersect: false },
                colors: ["#556ee6", "#34c38f"],
                legend: { position: "top" },
            };
        },
        topProducts() {
            return (this.sales_top && this.sales_top.top_products) || [];
        },
        topGroups() {
            return (this.sales_top && this.sales_top.top_groups) || [];
        },
    },
    mounted() {
        this.reload();
    },
    methods: {
        ...reportMethods,
        fmtMoney(v) {
            if (v == null) return "";
            return "R " + Number(v).toFixed(2);
        },
        reload() {
            if (!this.fromIso || !this.toIso) return;
            this.getSalesSummary(this.params).catch(() => {});
            this.getSalesTopProducts({ ...this.params, limit: this.limit }).catch(() => {});
        },
        async doExport() {
            this.exporting = true;
            try {
                await this.exportSalesXlsx(this.params);
            } finally {
                this.exporting = false;
            }
        },
    },
};
</script>
<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row mb-3">
            <div class="col-md-5">
                <label class="form-label small text-muted mb-1">Date range</label>
                <date-picker v-model="range" range value-type="format" format="YYYY-MM-DD" :clearable="false" />
            </div>
            <div class="col-md-3">
                <label class="form-label small text-muted mb-1">Bucket</label>
                <b-form-select v-model="bucket" :options="[
                    { value: 'day', text: 'Daily' },
                    { value: 'week', text: 'Weekly' },
                    { value: 'month', text: 'Monthly' },
                ]" />
            </div>
            <div class="col-md-4 d-flex align-items-end gap-2">
                <b-button variant="primary" @click="reload" :disabled="sales_loading" class="me-2">
                    <i class="fa fa-sync" /> Refresh
                </b-button>
                <b-button variant="success" @click="doExport" :disabled="exporting">
                    <i class="fa fa-file-excel" /> {{ exporting ? "Building..." : "Download Excel" }}
                </b-button>
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-md-3">
                <b-card><div class="text-muted small">Total Revenue</div><h3 class="mb-0">{{ fmtMoney(kpis.total_revenue) }}</h3></b-card>
            </div>
            <div class="col-md-3">
                <b-card><div class="text-muted small">Orders</div><h3 class="mb-0">{{ kpis.order_count }}</h3></b-card>
            </div>
            <div class="col-md-3">
                <b-card><div class="text-muted small">Avg Order Value</div><h3 class="mb-0">{{ fmtMoney(kpis.aov) }}</h3></b-card>
            </div>
            <div class="col-md-3">
                <b-card><div class="text-muted small">Units Sold</div><h3 class="mb-0">{{ kpis.units_sold }}</h3></b-card>
            </div>
        </div>

        <b-card class="mb-3">
            <h5 class="mb-3">Revenue & Orders</h5>
            <apexchart v-if="series.length" height="320" :series="chartSeries" :options="chartOptions" />
            <div v-else class="text-center text-muted py-5">No sales in this range.</div>
        </b-card>

        <div class="row">
            <div class="col-md-7">
                <b-card>
                    <h5 class="mb-3">Top Products</h5>
                    <b-table :items="topProducts" :fields="topProductFields" small striped responsive empty-text="No data" show-empty />
                </b-card>
            </div>
            <div class="col-md-5">
                <b-card>
                    <h5 class="mb-3">Top Groups</h5>
                    <b-table :items="topGroups" :fields="topGroupFields" small striped responsive empty-text="No data" show-empty />
                </b-card>
            </div>
        </div>

        <p class="small text-muted mt-2">
            <i class="fa fa-info-circle" /> Revenue per product/group uses current product prices (line-item prices are not snapshotted at sale time) — treat as approximate for historical ranges.
        </p>
    </Layout>
</template>
